import React, { useEffect } from 'react';
import { Form, Input, Button, Card, Row, Col, Typography, message, Divider } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { updateBDE } from '../../store/bdeSlice';
import { manualLogin } from '../../store/authSlice';
import { formatName } from '../../utils/formatters';

const { Title } = Typography;

const Profile = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [form] = Form.useForm();

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        ...user,
        // Remove +91 for display in input if desired, but let's keep it consistent
        // or just display as is if the user is used to it.
        // Actually, let's keep mobile read-only.
      });
    }
  }, [user, form]);

  const onFinish = async (values) => {
    try {
      const formattedName = formatName(values.name);

      const updatedUser = {
        ...user,
        ...values,
        name: formattedName,
        mobile: user.mobile, // Prevent mobile change
      };

      await dispatch(updateBDE(updatedUser)).unwrap();
      dispatch(manualLogin(updatedUser)); // Update local session

      message.success('Profile and bank details updated successfully');
    } catch (err) {
      message.error('Failed to update profile: ' + err);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>My Profile</Title>
        <Typography.Text type="secondary">Manage your personal information and bank details</Typography.Text>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          {/* Personal Information Section */}
          <div style={{ marginBottom: 24 }}>
            <Title level={4} style={{ marginBottom: 16, color: '#1e293b' }}>Personal Information</Title>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label="Full Name"
                  rules={[{ required: true, message: 'Please input your name!' }]}
                >
                  <Input placeholder="John Doe" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="mobile"
                  label="Mobile Number"
                  extra="Mobile number cannot be changed"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: 'Please input your email!' },
                    { type: 'email', message: 'Please enter a valid email!' }
                  ]}
                >
                  <Input placeholder="john@example.com" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="pincode"
                  label="Working Pincode"
                  rules={[
                    { required: true, message: 'Please input your pincode!' },
                    { pattern: /^[0-9]{6}$/, message: 'Please enter a valid 6-digit pincode!' }
                  ]}
                >
                  <Input placeholder="110001" maxLength={6} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="address"
                  label="Address"
                  rules={[{ required: true, message: 'Please input your address!' }]}
                >
                  <Input.TextArea rows={3} placeholder="Enter your full address" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Divider />

          {/* Bank Details Section */}
          <div style={{ marginBottom: 24 }}>
            <Title level={4} style={{ marginBottom: 16, color: '#1e293b' }}>Bank Details</Title>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="accountNumber"
                  label="AC No."
                  rules={[{ required: true, message: 'Please enter account number' }]}
                >
                  <Input placeholder="Enter account number" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="ifsc"
                  label="IFSC Code"
                  rules={[
                    { required: true, message: 'Please enter IFSC code' },
                    { pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/, message: 'Invalid IFSC Code format' }
                  ]}
                >
                  <Input placeholder="e.g. HDFC0001234" style={{ textTransform: 'uppercase' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="branchName"
                  label="Bank name"
                  rules={[{ required: true, message: 'Please enter bank name' }]}
                >
                  <Input placeholder="e.g. HDFC Bank" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="upiId"
                  label="UPI ID"
                  rules={[{ required: true, message: 'Please enter UPI ID' }]}
                >
                  <Input placeholder="e.g. john@upi" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Form.Item>
            <Button type="primary" htmlType="submit" size="large">
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Profile;
